import type { ComboVariation, ComboVariationWithPricing, PropostaItem } from '@/types'
import { formatCurrency } from '@/utils/format-currency'
import type { PlanServiceItem } from '@/utils/dealCalculations'
import { groupServicesByCategory } from '@/utils/dealCalculations'
import { resolveComboVariationPricing } from '@/utils/comboVariationPricing'
import { formatCategoryDisplayTitle, normalizeTextEncoding } from '@/utils/text-normalization'
import { resolveProposalItemScope, type CatalogPlan } from '@/utils/planCatalog'
import { resolvePlanEditorScope } from '../mergePlanServicesWithTemplate'

export type SlideOption = { label: string; value: number; type: string }

export type ComboSlideDescriptor = {
  variationIndex: number
  itemIndex: number
  item: PropostaItem
  variation: ComboVariationWithPricing
  isLastInVariation: boolean
}

export function getActiveComboVariationIndex(
  variationsWithPricing: ComboVariationWithPricing[],
  activeIndex: number,
): number {
  return flattenComboSlides(variationsWithPricing)[activeIndex]?.variationIndex ?? 0
}

export function flattenComboSlides(
  variationsWithPricing: ComboVariationWithPricing[],
): ComboSlideDescriptor[] {
  const slides: ComboSlideDescriptor[] = []
  variationsWithPricing.forEach((variation, variationIndex) => {
    variation.items.forEach((item, itemIndex) => {
      slides.push({
        variationIndex,
        itemIndex,
        item,
        variation,
        isLastInVariation: itemIndex === variation.items.length - 1,
      })
    })
  })
  return slides
}

export function buildSlideOptions(
  comboVariations: ComboVariation[],
  proposalItems: PropostaItem[],
  variationsWithPricing: ComboVariationWithPricing[],
): SlideOption[] {
  if (comboVariations.length === 0) {
    return proposalItems.map((item, idx) => ({
      label: `${item.descricao} (${item.tipo === 'plano' ? 'Plano' : item.tipo === 'servico' ? 'Setup' : 'Avulso'})`,
      value: idx,
      type: 'item',
    }))
  }

  const multiVariation = variationsWithPricing.length > 1
  return flattenComboSlides(variationsWithPricing).map((slide, idx) => ({
    label: multiVariation
      ? `${slide.variation.nome} — ${slide.item.descricao || slide.item.name || 'Plano'}`
      : slide.item.descricao || slide.item.name || slide.variation.nome || 'Plano',
    value: idx,
    type: 'combo-item',
  }))
}

function buildPlanFromItem(
  rawPlan: PropostaItem,
  catalogPlans: CatalogPlan[],
): { activePlan: PropostaItem; activeServiceGroups: Map<string, PlanServiceItem[]> } {
  const editorScope = resolvePlanEditorScope(rawPlan, catalogPlans)
  const scope = editorScope.length > 0 ? editorScope : resolveProposalItemScope(rawPlan, catalogPlans)
  const plan = { ...rawPlan, services: scope }
  const planServices: PlanServiceItem[] = []

  if (scope.length > 0) {
    scope.forEach((cat: { category?: string; categoria?: string; services?: unknown[]; servicos?: unknown[] }) => {
      const catName = formatCategoryDisplayTitle(cat.category || cat.categoria || 'Geral')
      const servicesList = (cat.services || cat.servicos || []) as Array<{
        id?: string
        name?: string
        nome?: string
        isIncluded?: boolean | null
        isPartial?: boolean
        quantity?: string | number
        quantidade?: string | number
      }>
      servicesList.forEach((svc) => {
        const included =
          svc.isIncluded === undefined || svc.isIncluded === null ? true : !!svc.isIncluded
        if (included) {
          planServices.push({
            id: svc.id || `${plan.id}-${svc.name || svc.nome}`,
            nome: normalizeTextEncoding(svc.name || svc.nome || ''),
            valor: 0,
            categoria: normalizeTextEncoding(catName),
            recorrente: plan.tipo === 'plano' || plan.tipo === 'servico',
            isPartial: svc.isPartial,
            quantity: svc.quantity || svc.quantidade,
          } as PlanServiceItem)
        }
      })
    })
  } else {
    planServices.push({
      id: plan.id,
      nome: plan.descricao,
      valor: plan.valorUnitario,
      categoria: plan.tipo === 'plano' ? 'Planos' : plan.tipo === 'servico' ? 'Serviços' : 'Avulsos',
      recorrente: plan.tipo === 'plano' || plan.tipo === 'servico',
    })
  }

  return { activePlan: plan, activeServiceGroups: groupServicesByCategory(planServices) }
}

function applyVariationComboPricing(plan: PropostaItem, variation: ComboVariationWithPricing): PropostaItem {
  const hasDiscount = variation.valorOriginal > variation.valorFinal + 0.009
  const discountLabel =
    variation.precoFechado !== undefined
      ? 'PREÇO ESPECIAL'
      : variation.descontoPercentual
        ? `${variation.descontoPercentual}% OFF`
        : null
  const resolved = resolveComboVariationPricing(variation)

  return {
    ...plan,
    duration: resolved.installments,
    pricing: {
      total: resolved.total,
      entry: resolved.entry,
      installments: resolved.installments,
      installmentValue: resolved.installmentValue,
      recorrente: resolved.recorrente,
    },
    showBonus: hasDiscount,
    bonus: hasDiscount && discountLabel
      ? `ECONOMIA DE ${formatCurrency(variation.valorOriginal - variation.valorFinal)} (${discountLabel})`
      : plan.bonus,
    comboFinalPrice: resolved.installmentValue,
  }
}

export function computeActivePlanContext(params: {
  comboVariations: ComboVariation[]
  proposalItems: PropostaItem[]
  variationsWithPricing: ComboVariationWithPricing[]
  activeIndex: number
  catalogPlans: CatalogPlan[]
}): {
  activePlan: PropostaItem | null
  activeServiceGroups: Map<string, PlanServiceItem[]>
  showPricing: boolean
  comboPriceFrom?: number
  comboPriceTo?: number
} {
  const { comboVariations, proposalItems, variationsWithPricing, activeIndex, catalogPlans } =
    params

  if (comboVariations.length === 0) {
    const rawPlan = proposalItems[activeIndex] || proposalItems[0] || null
    if (!rawPlan) {
      return { activePlan: null, activeServiceGroups: new Map(), showPricing: true }
    }
    const built = buildPlanFromItem(rawPlan, catalogPlans)
    return { ...built, showPricing: true }
  }

  const slide = flattenComboSlides(variationsWithPricing)[activeIndex]
  if (!slide) {
    return { activePlan: null, activeServiceGroups: new Map(), showPricing: false }
  }

  let rawPlan = slide.item
  if (slide.isLastInVariation) {
    rawPlan = applyVariationComboPricing(rawPlan, slide.variation)
  }

  const built = buildPlanFromItem(rawPlan, catalogPlans)
  const showPricing = slide.isLastInVariation
  return {
    ...built,
    showPricing,
    comboPriceFrom: showPricing ? slide.variation.valorOriginal : undefined,
    comboPriceTo: showPricing ? slide.variation.valorFinal : undefined,
  }
}

export function computeProposalTotals(proposalItems: PropostaItem[]) {
  return proposalItems.reduce(
    (acc, item) => {
      const isRecurring = item.tipo === 'plano' || item.tipo === 'servico'
      if (isRecurring) acc.valorMensal += item.total
      else acc.valorSetup += item.total
      acc.valorTotal += item.total
      return acc
    },
    { valorTotal: 0, valorMensal: 0, valorSetup: 0 },
  )
}
